/**
 * Wallet-Credit Vouchers — admin: generate vouchers that grant SPENDABLE
 * wallet balance (real money, usable for predictions). Backend: versusapp PR4
 * (feat/wallet-credit-vouchers, merged).
 *
 * Access gate: page entry requires the current Dubai-time HHMM (UTC+4, ±1
 * minute window). Convention only — both client and server compute the
 * code, the prompt is the knowledge filter. Server enforces the same code
 * server-side on every generate request via X-Versus-Time-Code header.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import './VouchersPage.css';
import './WalletCreditVouchersPage.css';

// ============================================================================
// Dubai time-code utilities (mirrors backend src/utils/dubaiTimeCode.ts)
// ============================================================================

const DUBAI_OFFSET_MINUTES = 4 * 60;
const SKEW_MINUTES = 1;

function fmtHHMM(minutesOfDay: number): string {
  const wrapped = ((minutesOfDay % 1440) + 1440) % 1440;
  const hh = Math.floor(wrapped / 60);
  const mm = wrapped % 60;
  return `${String(hh).padStart(2, '0')}${String(mm).padStart(2, '0')}`;
}

function currentDubaiTimeCode(): string {
  const now = new Date();
  return fmtHHMM(now.getUTCHours() * 60 + now.getUTCMinutes() + DUBAI_OFFSET_MINUTES);
}

function dubaiCodeWindow(): string[] {
  const now = new Date();
  const base = now.getUTCHours() * 60 + now.getUTCMinutes() + DUBAI_OFFSET_MINUTES;
  const codes: string[] = [];
  for (let d = -SKEW_MINUTES; d <= SKEW_MINUTES; d++) codes.push(fmtHHMM(base + d));
  return codes;
}

function isInDubaiWindow(input: string): boolean {
  const trimmed = input.trim();
  if (!/^\d{4}$/.test(trimmed)) return false;
  return dubaiCodeWindow().includes(trimmed);
}

// ============================================================================
// Voucher type for the listing (subset of full voucher row)
// ============================================================================

interface WalletCreditVoucher {
  id: string;
  code: string;
  feeCreditAmount: number;
  feeCreditAmountCents: string;
  currency: string;
  maxRedemptions: number;
  redemptionCount: number;
  voucherValidUntil: string;
  status: string;
  createdBy: string;
  internalLabel: string | null;
  creditsWithdrawable: boolean;
  createdAt: string;
}

const SUPPORTED_CURRENCIES = ['EUR', 'USD', 'GBP'] as const;
type SupportedCurrency = typeof SUPPORTED_CURRENCIES[number];

const PAGE_SIZE = 50;

// ============================================================================
// Component
// ============================================================================

const WalletCreditVouchersPage: React.FC = () => {
  const [unlocked, setUnlocked] = useState(false);
  return unlocked ? <UnlockedPage /> : <TimeCodePrompt onUnlock={() => setUnlocked(true)} />;
};

// ----------------------------------------------------------------------------
// Time-code prompt
// ----------------------------------------------------------------------------

const TimeCodePrompt: React.FC<{ onUnlock: () => void }> = ({ onUnlock }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (isInDubaiWindow(code)) {
      onUnlock();
    } else {
      // Generic rejection — convention is intentionally undocumented in
      // the UI. Anyone authorised to use this page knows the PIN format
      // out-of-band; everyone else stays out.
      setError('Incorrect PIN.');
    }
  };

  return (
    <div className="vouchers-page wcv-prompt-wrapper">
      <div className="page-head">
        <div>
          <h1 className="page-title">🔒 Restricted</h1>
          <div className="page-sub">Enter PIN to continue.</div>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <form onSubmit={submit} className="wcv-prompt-form">
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{4}"
              maxLength={4}
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
              placeholder="••••"
              className="wcv-prompt-input"
            />
            {error && <div className="alert alert-error" style={{ marginTop: 12 }}>{error}</div>}
            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button type="submit" className="btn btn-primary" disabled={code.length !== 4}>
                Unlock
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Unlocked page — list + generate
// ----------------------------------------------------------------------------

const UnlockedPage: React.FC = () => {
  const [vouchers, setVouchers] = useState<WalletCreditVoucher[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterLabel, setFilterLabel] = useState<string>('');
  const [labelInput, setLabelInput] = useState<string>('');
  const [offset, setOffset] = useState(0);

  const [showGenerate, setShowGenerate] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState<string[] | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: {
        voucherType: 'wallet_credit';
        status?: 'active' | 'paused' | 'expired' | 'revoked' | 'exhausted';
        label?: string;
        limit: number;
        offset: number;
      } = { voucherType: 'wallet_credit', limit: PAGE_SIZE, offset };
      if (filterStatus) params.status = filterStatus as 'active' | 'paused' | 'expired' | 'revoked' | 'exhausted';
      if (filterLabel) params.label = filterLabel;
      const result = await apiService.listVouchers(params);
      setVouchers(result.vouchers as WalletCreditVoucher[]);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load wallet-credit vouchers');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterLabel, offset]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="vouchers-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">💰 Wallet-credit vouchers</h1>
          <div className="page-sub">
            {total} total · grants SPENDABLE balance · audited per issuance · max €500/voucher · max €50,000/batch
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowGenerate(true)}>+ Generate codes</button>
      </div>

      {error && <div className="alert alert-error" onClick={() => setError(null)}>{error}</div>}
      {success && <div className="alert alert-success" onClick={() => setSuccess(null)}>{success}</div>}

      <div className="card">
        <div className="card-body">
          <div className="filters">
            <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setOffset(0); }}>
              <option value="">All statuses</option>
              <option value="active">active</option>
              <option value="paused">paused</option>
              <option value="exhausted">exhausted</option>
              <option value="expired">expired</option>
              <option value="revoked">revoked</option>
            </select>
            <input
              type="text"
              placeholder="Filter by label (substring)"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setFilterLabel(labelInput.trim()); setOffset(0); } }}
              onBlur={() => { if (labelInput.trim() !== filterLabel) { setFilterLabel(labelInput.trim()); setOffset(0); } }}
              className="filter-input"
            />
            {(filterLabel || filterStatus) && (
              <button className="btn btn-small btn-secondary" onClick={() => {
                setFilterStatus(''); setFilterLabel(''); setLabelInput(''); setOffset(0);
              }}>Clear filters</button>
            )}
          </div>

          {loading ? (
            <div className="loading">Loading…</div>
          ) : vouchers.length === 0 ? (
            <div className="empty">No wallet-credit vouchers issued yet for this filter.</div>
          ) : (
            <table className="vouchers-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Label</th>
                  <th>Amount</th>
                  <th>Withdrawable</th>
                  <th>Used / Max</th>
                  <th>Valid until</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((v) => (
                  <tr key={v.id}>
                    <td><code>{v.code}</code></td>
                    <td className="label-cell" title={v.internalLabel ?? ''}>{v.internalLabel ?? <span className="muted">—</span>}</td>
                    <td>{v.feeCreditAmount.toFixed(2)} {v.currency}</td>
                    <td>{v.creditsWithdrawable ? <span className="status status-paused">withdrawable</span> : <span className="muted">bonus_balance</span>}</td>
                    <td>{v.redemptionCount} / {v.maxRedemptions}</td>
                    <td>{new Date(v.voucherValidUntil).toLocaleDateString()}</td>
                    <td><span className={`status status-${v.status}`}>{v.status}</span></td>
                    <td>{new Date(v.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="pagination">
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
            <span>{Math.min(offset + 1, total)}–{Math.min(offset + PAGE_SIZE, total)} of {total}</span>
            <button disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
          </div>
        </div>
      </div>

      {showGenerate && (
        <GenerateModal
          onClose={() => { setShowGenerate(false); setGeneratedCodes(null); }}
          onGenerated={(codes) => {
            setGeneratedCodes(codes);
            setSuccess(`Generated ${codes.length} wallet-credit code${codes.length === 1 ? '' : 's'}`);
            void load();
          }}
          generatedCodes={generatedCodes}
        />
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Generate modal — wallet-credit specific
// ----------------------------------------------------------------------------

interface GenerateProps {
  onClose: () => void;
  onGenerated: (codes: string[]) => void;
  generatedCodes: string[] | null;
}

const GenerateModal: React.FC<GenerateProps> = ({ onClose, onGenerated, generatedCodes }) => {
  const [feeCreditAmount, setFeeCreditAmount] = useState<string>('25.00');
  const [currency, setCurrency] = useState<SupportedCurrency>('EUR');
  const [internalLabel, setInternalLabel] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [validUntilDate, setValidUntilDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [feeCreditExpiresInDays, setFeeCreditExpiresInDays] = useState<number>(90);
  const [perUserLimit, setPerUserLimit] = useState<number>(1);
  const [creditsWithdrawable, setCreditsWithdrawable] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reasonValid = reason.trim().length >= 20;
  const cents = Math.round(parseFloat(feeCreditAmount) * 100);
  const amountValid = Number.isFinite(cents) && cents > 0 && cents <= 50_000;
  const totalCents = cents * quantity;
  const totalValid = totalCents <= 5_000_000;

  const submit = async () => {
    setErr(null);
    if (!amountValid) { setErr('Amount must be > 0 and ≤ €500 per voucher'); return; }
    if (!totalValid) { setErr('Batch total must be ≤ €50,000 (amount × quantity)'); return; }
    if (!reasonValid) { setErr('Reason ≥ 20 chars required for audit'); return; }

    setSubmitting(true);
    try {
      // Fresh time-code at request time (rotates per minute server-side too).
      const result = await apiService.generateWalletCreditVouchers(
        {
          feeCreditAmountCents: String(cents),
          quantity,
          voucherValidUntil: new Date(`${validUntilDate}T23:59:59Z`).toISOString(),
          feeCreditExpiresInDays,
          perUserLimit,
          maxRedemptionsPerCode: 1,
          requiresKyc: true,
          minAccountAgeDays: 0,
          currency,
          internalLabel: internalLabel.trim() || undefined,
          creditsWithdrawable,
          reason: reason.trim(),
        },
        { timeCode: currentDubaiTimeCode() }
      );
      onGenerated(result.codes);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Generation failed';
      setErr(msg.includes('time-code')
        ? `${msg} — your unlock may have aged out (≥ 2 min). Refresh and re-enter the current code.`
        : msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (generatedCodes) {
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h2>Generated {generatedCodes.length} wallet-credit code{generatedCodes.length === 1 ? '' : 's'}</h2>
          <p className="modal-sub">
            Audit row written. These codes grant <strong>{creditsWithdrawable ? 'withdrawable' : 'non-withdrawable, betting-usable'}</strong> wallet balance.
          </p>
          <textarea readOnly className="codes-output" value={generatedCodes.join('\n')} />
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => navigator.clipboard.writeText(generatedCodes.join('\n'))}>Copy all</button>
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Generate wallet-credit vouchers</h2>
        <p className="modal-sub">⚠️ This generates real spendable balance. Each issuance writes to <code>wallet_credit_voucher_audit</code> with your admin id, IP, and reason.</p>
        {err && <div className="alert alert-error">{err}</div>}
        <div className="form-grid">
          <label>
            <span>Currency</span>
            <select value={currency} onChange={(e) => setCurrency(e.target.value as SupportedCurrency)}>
              {SUPPORTED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>
            <span>Amount per code ({currency}) — max 500.00</span>
            <input type="text" inputMode="decimal" value={feeCreditAmount} onChange={(e) => setFeeCreditAmount(e.target.value)} />
          </label>
          <label>
            <span>Quantity (max 100/batch, max €50K total)</span>
            <input type="number" min={1} max={100} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} />
          </label>
          <label>
            <span>Voucher valid until</span>
            <input type="date" value={validUntilDate} onChange={(e) => setValidUntilDate(e.target.value)} />
          </label>
          <label>
            <span>Per-user limit</span>
            <input type="number" min={1} value={perUserLimit} onChange={(e) => setPerUserLimit(Number(e.target.value))} />
          </label>
          <label>
            <span>Credit expires (days, info-only — wallet credit is permanent)</span>
            <input type="number" min={1} max={3650} value={feeCreditExpiresInDays} onChange={(e) => setFeeCreditExpiresInDays(Number(e.target.value))} />
          </label>
          <label className="checkbox-row" style={{ gridColumn: '1 / span 2' }}>
            <input type="checkbox" checked={creditsWithdrawable} onChange={(e) => setCreditsWithdrawable(e.target.checked)} />
            <span>
              <strong>Withdrawable</strong> — credits to <code>wallets.balance</code> (cash-out path).
              Default <strong>off</strong> → credits to <code>wallets.bonus_balance</code> (UKGC-friendly,
              non-withdrawable but usable for predictions).
            </span>
          </label>
          <label className="checkbox-row" style={{ flexDirection: 'column', alignItems: 'stretch', gridColumn: '1 / span 2' }}>
            <span>Internal label (filter / partner attribution)</span>
            <input type="text" maxLength={200} value={internalLabel} onChange={(e) => setInternalLabel(e.target.value)} placeholder="e.g. Apr-2026 power-user comp" />
          </label>
          <label className="checkbox-row" style={{ flexDirection: 'column', alignItems: 'stretch', gridColumn: '1 / span 2' }}>
            <span>Reason (≥ 20 chars, audit trail — UKGC will ask)</span>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="reason-input"
              placeholder="Why are you issuing this? Be specific."
            />
            <small className="muted" style={{ marginTop: 4 }}>{reason.trim().length} / 20+ chars</small>
          </label>
          <div className="wcv-summary" style={{ gridColumn: '1 / span 2' }}>
            <strong>Total:</strong> {(totalCents / 100).toFixed(2)} {currency}
            {' · '}
            <strong>Type:</strong> {creditsWithdrawable ? 'withdrawable wallet balance' : 'bonus_balance (bet-only)'}
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={submitting || !amountValid || !totalValid || !reasonValid}
          >
            {submitting ? 'Generating…' : `Generate ${quantity} code${quantity === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WalletCreditVouchersPage;
